/**
 * Course email templates that ride on section reminder emails.
 *
 * A template under `email-templates-per-course/{courseId}/docs` becomes a
 * section reminder note when it carries a {@link SECTION_REMINDER_NOTE_FIELD}
 * map: its `html` is added to the course's reminders for that course week.
 * The template editor keeps the map, since it saves only the fields it edits
 * with `merge: true`. A note only rides on a reminder the reminder cron
 * already sends; it never causes a send.
 */

import {z} from 'zod';

/** The template field that makes a course email template a reminder note. */
export const SECTION_REMINDER_NOTE_FIELD = 'sectionReminder';

/** The reminder firings a note can ride on, named as the reminder cron does. */
export const SECTION_REMINDER_NOTE_TIMINGS = [
  'oneDayBeforeSection',
  'threeHoursBeforeSection',
  'oneHourBeforeSection',
] as const;

/** One of the {@link SECTION_REMINDER_NOTE_TIMINGS}. */
export type SectionReminderNoteTiming =
  (typeof SECTION_REMINDER_NOTE_TIMINGS)[number];

/** The reminder recipients a note can address. */
export const SECTION_REMINDER_NOTE_ROLES = [
  'student',
  'sectionLeader',
] as const;

/** One of the {@link SECTION_REMINDER_NOTE_ROLES}. */
export type SectionReminderNoteRole =
  (typeof SECTION_REMINDER_NOTE_ROLES)[number];

/** Schema of a template's {@link SECTION_REMINDER_NOTE_FIELD} map. */
export const SectionReminderNoteSettingsSchema = z.object({
  /** The 1-based course week whose reminders carry the note. */
  courseWeek: z.number().int().min(1),
  /** The firings the note rides on. Absent: the one-day reminder. */
  timings: z
    .array(z.enum(SECTION_REMINDER_NOTE_TIMINGS))
    .min(1)
    .default(['oneDayBeforeSection']),
  /** The recipients the note addresses. Absent: students. */
  roles: z
    .array(z.enum(SECTION_REMINDER_NOTE_ROLES))
    .min(1)
    .default(['student']),
  /**
   * An assignment id. Set, the note goes only to recipients who have not
   * completed that assignment in this course. Absent, it goes to everyone
   * the timing and role select.
   */
  unlessAssignmentCompleted: z.string().trim().min(1).optional(),
});

/** A template's {@link SECTION_REMINDER_NOTE_FIELD} map, defaults applied. */
export type SectionReminderNoteSettings = z.infer<
  typeof SectionReminderNoteSettingsSchema
>;

const SectionReminderNoteTemplateSchema = z.object({
  html: z.string().trim().min(1),
  archived: z.boolean().optional(),
  [SECTION_REMINDER_NOTE_FIELD]: SectionReminderNoteSettingsSchema,
});

/** A course email template that is an active section reminder note. */
export interface SectionReminderNote {
  /** The template's id within the course's templates. */
  templateId: string;
  /** The template's stored HTML, a Handlebars source. */
  html: string;
  /** When and to whom the note is added. */
  settings: SectionReminderNoteSettings;
}

/** Outcome of reading one template as a reminder note. */
export type ParsedSectionReminderNote =
  | {status: 'active'; note: SectionReminderNote}
  | {status: 'archived'}
  | {status: 'invalid'; issues: string};

/**
 * Reads one course email template as a section reminder note.
 *
 * @param args - Inputs.
 * @param args.templateId - The template's document id.
 * @param args.data - The template document's data.
 * @return `active` with the note; `archived` for an archived template, which
 *   is how a note is switched off from the template editor; or `invalid`
 *   with the validation issues when the template has no HTML or its
 *   {@link SECTION_REMINDER_NOTE_FIELD} map is malformed or missing.
 * @throws Never.
 */
export function parseSectionReminderNote({
  templateId,
  data,
}: {
  templateId: string;
  data: unknown;
}): ParsedSectionReminderNote {
  const parsed = SectionReminderNoteTemplateSchema.safeParse(data);
  if (!parsed.success) {
    return {status: 'invalid', issues: z.prettifyError(parsed.error)};
  }
  if (parsed.data.archived === true) {
    return {status: 'archived'};
  }
  return {
    status: 'active',
    note: {
      templateId,
      html: parsed.data.html,
      settings: parsed.data[SECTION_REMINDER_NOTE_FIELD],
    },
  };
}

/**
 * Whether a note rides on one reminder.
 *
 * @param args - Inputs.
 * @param args.note - An active note, from {@link parseSectionReminderNote}.
 * @param args.courseWeek - The 1-based course week of the upcoming section.
 * @param args.timing - Which reminder firing this is.
 * @param args.role - Who the reminder is addressed to.
 * @return Whether the note's week, timings and roles all match. Whether the
 *   recipient has met `unlessAssignmentCompleted` is the caller's to check.
 * @throws Never.
 */
export function sectionReminderNoteApplies({
  note,
  courseWeek,
  timing,
  role,
}: {
  note: SectionReminderNote;
  courseWeek: number;
  timing: SectionReminderNoteTiming;
  role: SectionReminderNoteRole;
}): boolean {
  const {settings} = note;
  return (
    settings.courseWeek === courseWeek &&
    settings.timings.includes(timing) &&
    settings.roles.includes(role)
  );
}
